import { pendingCount } from './attempt-queue.js';
import { cancelSpeech } from './speech.js';

/**
 * Picking up a new version without losing anything.
 *
 * An iPad keeps the app open for days, so a deploy used to reach it only when
 * somebody happened to close it - the old screen kept running the old code
 * against the new server. The service worker is now in 'prompt' mode: a new
 * version installs and WAITS, and the app decides when to switch.
 *
 * It switches only between questions: when the child taps "Next one", on the
 * home screen, or on the mission-complete screen. Never with a question on
 * screen, and never while an answer is still waiting to be sent - an answer
 * saved offline lives in IndexedDB and is flushed when the connection returns,
 * so a reload would not lose it, but it would race it, and there is no reason
 * to.
 */

let apply: (() => void) | null = null;

/** Called by the service worker registration when a new version is waiting. */
export function markUpdateReady(applyUpdate: () => void): void {
  apply = applyUpdate;
}

export function isUpdateReady(): boolean {
  return apply !== null;
}

/**
 * Switches to the waiting version if there is one and nothing is in flight.
 * Resolves true when the page is about to reload, so the caller does nothing
 * else.
 */
export async function reloadIfUpdateWaiting(): Promise<boolean> {
  if (!apply) return false;
  const queued = await pendingCount().catch(() => 1);
  if (queued > 0 || !apply) return false;
  const go = apply;
  apply = null;
  cancelSpeech();
  go();
  return true;
}

/**
 * Asks the server for a new version every half hour, and whenever the iPad is
 * picked up again. In 'prompt' mode finding one only marks it ready.
 */
export function watchForUpdates(
  registration: { update(): Promise<unknown> },
  everyMs = 30 * 60 * 1000,
): () => void {
  const check = () => void registration.update().catch(() => undefined);
  const timer = setInterval(check, everyMs);
  const onShow = () => {
    if (document.visibilityState === 'visible') check();
  };
  document.addEventListener('visibilitychange', onShow);
  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onShow);
  };
}

export { CLIENT_BUILD } from './build.js';
